import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { getBboxServiceUrl } from "lib/utils/connection";
import { authedFetch } from "lib/utils/authedFetch";

export type RecognitionEngineId = "yolo26";

export type RecognitionToggleResponse = {
    enabled: boolean;
};

/** Per-engine reachability + enabled flag from a soft probe. */
export type RecognitionEngineStatus = {
    up: boolean;
    enabled: boolean;
};

export type RecognitionEnginesStatus = Record<
    RecognitionEngineId,
    RecognitionEngineStatus
>;

async function parseError(res: Response): Promise<string> {
    let detail = res.statusText;
    try {
        const err = (await res.json()) as { error?: string; detail?: unknown };
        if (typeof err.error === "string" && err.error) detail = err.error;
        else if (typeof err.detail === "string" && err.detail) detail = err.detail;
    } catch {
        /* ignore */
    }
    return toUserFacingApiError(detail, "Couldn't complete the object recognition request. Try again.");
}

function requireServiceUrl(url: string | null): string {
    if (!url) throw new Error("Object recognition is unavailable.");
    return url;
}

/**
 * Detect whether the recognition service is present without requiring it to be
 * healthy or enabled. A non-404 response means the route exists; authentication,
 * method, and service-state errors are still evidence that the service is deployed.
 */
export async function probeObjectRecognitionService(
    host: string,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<boolean> {
    try {
        const res = await authedFetch(
            requireServiceUrl(getBboxServiceUrl(host, "recognition", isSecure, arcId)),
            { cache: "no-store" },
        );
        return res.status !== 404;
    } catch {
        return false;
    }
}

/** Unreachable / failed PUT → treat as already off (dead process cannot hold SHM). */
async function tryDisable(put: () => Promise<void>): Promise<void> {
    try {
        await put();
    } catch {
        /* ignore */
    }
}

export async function fetchYoloRecognition(
    host: string,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<RecognitionToggleResponse> {
    const res = await authedFetch(requireServiceUrl(getBboxServiceUrl(host, "recognition", isSecure, arcId)));
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as { enabled?: boolean };
    return { enabled: data.enabled !== false };
}

export async function putYoloRecognition(
    host: string,
    enabled: boolean,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<void> {
    const res = await authedFetch(requireServiceUrl(getBboxServiceUrl(host, "recognition", isSecure, arcId)), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
    });
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
}

/**
 * Soft-probe YOLO bbox service (:8030).
 * Down engine reports `{ up: false, enabled: false }` without throwing.
 */
export async function probeRecognitionEngines(
    host: string,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<RecognitionEnginesStatus> {
    const yolo = await fetchYoloRecognition(host, isSecure, arcId)
        .then((r) => ({ up: true, enabled: r.enabled }))
        .catch(() => ({ up: false, enabled: false }));
    return { yolo26: yolo };
}

/**
 * Pause every recognition engine. Unreachable engines are ignored
 * (they cannot hold video_pipeline's single-client SHM).
 */
export async function disableAllRecognitionEngines(
    host: string,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<void> {
    await tryDisable(() => putYoloRecognition(host, false, isSecure, arcId));
}

/** Enable YOLO recognition on the bbox inferencer (rtsp_inference_module). */
export async function applyBboxOverlaySource(
    host: string,
    _source: RecognitionEngineId = "yolo26",
    isSecure?: boolean,
    arcId?: string | null,
): Promise<void> {
    await putYoloRecognition(host, true, isSecure, arcId);
}

export type BboxPromptsPayload = {
    items: string[];
    threshold: number[];
    reload_on_change?: boolean;
};

export async function fetchBboxPrompts(
    host: string,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<BboxPromptsPayload> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "prompts", isSecure, arcId)),
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as {
        items?: unknown;
        threshold?: unknown;
        reload_on_change?: unknown;
    };
    const items = Array.isArray(data.items)
        ? data.items.map((x) => String(x).trim()).filter(Boolean)
        : [];
    const threshold = Array.isArray(data.threshold)
        ? data.threshold.map((x) => {
              const n = parseFloat(String(x));
              return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0.1;
          })
        : items.map(() => 0.1);
    return {
        items,
        threshold,
        reload_on_change: data.reload_on_change !== false,
    };
}

export async function putBboxPrompts(
    host: string,
    payload: BboxPromptsPayload,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<void> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "prompts", isSecure, arcId)),
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                items: payload.items,
                threshold: payload.threshold,
                reload_on_change: payload.reload_on_change ?? true,
            }),
        },
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
}

export type InferSettingsPayload = {
    imgsz: number | [number, number];
    imgsz_width: number;
    imgsz_height: number;
    imgsz_min?: number;
    imgsz_max?: number;
    bbox_overlay_color?: string;
};

function parseImgszDims(data: Record<string, unknown>): {
    width: number;
    height: number;
} {
    const min = 160;
    const max = 1920;
    const clamp = (n: number) => Math.max(min, Math.min(max, Math.round(n)));

    const wRaw = Number(data.imgsz_width);
    const hRaw = Number(data.imgsz_height);
    if (Number.isFinite(wRaw) && wRaw > 0 && Number.isFinite(hRaw) && hRaw > 0) {
        return { width: clamp(wRaw), height: clamp(hRaw) };
    }

    const imgsz = data.imgsz;
    if (Array.isArray(imgsz) && imgsz.length === 2) {
        const h = Number(imgsz[0]);
        const w = Number(imgsz[1]);
        if (Number.isFinite(h) && h > 0 && Number.isFinite(w) && w > 0) {
            return { width: clamp(w), height: clamp(h) };
        }
    }
    const edge = Number(imgsz);
    if (Number.isFinite(edge) && edge > 0) {
        const v = clamp(edge);
        return { width: v, height: v };
    }
    throw new Error("Invalid settings payload: missing imgsz");
}

export async function fetchInferSettings(
    host: string,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<InferSettingsPayload> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "settings", isSecure, arcId)),
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as Record<string, unknown>;
    const { width, height } = parseImgszDims(data);
    return {
        imgsz: width === height ? width : [height, width],
        imgsz_width: width,
        imgsz_height: height,
        imgsz_min: Number.isFinite(Number(data.imgsz_min))
            ? Math.round(Number(data.imgsz_min))
            : undefined,
        imgsz_max: Number.isFinite(Number(data.imgsz_max))
            ? Math.round(Number(data.imgsz_max))
            : undefined,
        bbox_overlay_color:
            typeof data.bbox_overlay_color === "string"
                ? data.bbox_overlay_color
                : undefined,
    };
}

export async function putInferImgsz(
    host: string,
    size: { width: number; height: number },
    isSecure?: boolean,
    arcId?: string | null,
): Promise<{ width: number; height: number }> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "settings", isSecure, arcId)),
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                imgsz_width: Math.round(size.width),
                imgsz_height: Math.round(size.height),
            }),
        },
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as Record<string, unknown>;
    try {
        return parseImgszDims(data);
    } catch {
        return {
            width: Math.round(size.width),
            height: Math.round(size.height),
        };
    }
}

export type InferModelEntry = {
    name: string;
    open_vocab: boolean;
};

export type InferenceSourceId = "front" | "gimbal";

export type InferenceSourceEntry = {
    id: InferenceSourceId;
    label: string;
    shm_path: string;
    ready: boolean;
};

export type InferenceSourcePayload = {
    source: InferenceSourceId;
    available: InferenceSourceEntry[];
};

export async function fetchInferSource(
    host: string,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<InferenceSourcePayload> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "source", isSecure, arcId)),
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as {
        source?: string;
        available?: Array<Record<string, unknown>>;
    };
    const sourceRaw = typeof data.source === "string" ? data.source.trim().toLowerCase() : "front";
    const source: InferenceSourceId = sourceRaw === "gimbal" ? "gimbal" : "front";
    const available: InferenceSourceEntry[] = Array.isArray(data.available)
        ? data.available
              .map((row) => {
                  const idRaw = typeof row.id === "string" ? row.id.trim().toLowerCase() : "";
                  if (idRaw !== "front" && idRaw !== "gimbal") return null;
                  return {
                      id: idRaw as InferenceSourceId,
                      label: typeof row.label === "string" ? row.label : idRaw,
                      shm_path: typeof row.shm_path === "string" ? row.shm_path : "",
                      ready: row.ready === true,
                  };
              })
              .filter((row): row is InferenceSourceEntry => row !== null)
        : [];
    return { source, available };
}

export async function putInferSource(
    host: string,
    source: InferenceSourceId,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<InferenceSourcePayload> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "source", isSecure, arcId)),
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ source }),
        },
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as {
        source?: string;
        available?: Array<Record<string, unknown>>;
    };
    const sourceRaw = typeof data.source === "string" ? data.source.trim().toLowerCase() : source;
    const resolved: InferenceSourceId = sourceRaw === "gimbal" ? "gimbal" : "front";
    const available: InferenceSourceEntry[] = Array.isArray(data.available)
        ? data.available
              .map((row) => {
                  const idRaw = typeof row.id === "string" ? row.id.trim().toLowerCase() : "";
                  if (idRaw !== "front" && idRaw !== "gimbal") return null;
                  return {
                      id: idRaw as InferenceSourceId,
                      label: typeof row.label === "string" ? row.label : idRaw,
                      shm_path: typeof row.shm_path === "string" ? row.shm_path : "",
                      ready: row.ready === true,
                  };
              })
              .filter((row): row is InferenceSourceEntry => row !== null)
        : [];
    return { source: resolved, available };
}

export type PostProcessConfig = import("lib/consts/postProcessRules").PostProcessConfig;
export type PostProcessCatalog = import("lib/consts/postProcessRules").PostProcessCatalog;
export type PostProcessPreset = import("lib/consts/postProcessRules").PostProcessPreset;

export async function fetchPostprocessCatalog(
    host: string,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<PostProcessCatalog> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "postprocess/catalog", isSecure, arcId)),
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    return (await res.json()) as PostProcessCatalog;
}

export async function fetchPostprocessRules(
    host: string,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<PostProcessConfig> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "postprocess/rules", isSecure, arcId)),
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    return (await res.json()) as PostProcessConfig;
}

export async function putPostprocessRules(
    host: string,
    config: PostProcessConfig,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<PostProcessConfig> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "postprocess/rules", isSecure, arcId)),
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(config),
        },
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as { postprocess?: PostProcessConfig };
    return data.postprocess ?? config;
}

export async function fetchPostprocessPresets(
    host: string,
    isSecure?: boolean,
    arcId?: string | null,
): Promise<{ presets: PostProcessPreset[] }> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "postprocess/presets", isSecure, arcId)),
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    return (await res.json()) as { presets: PostProcessPreset[] };
}

export async function applyPostprocessPreset(
    host: string,
    presetId: string,
    mode: "replace" | "append" = "replace",
    isSecure?: boolean,
    arcId?: string | null,
): Promise<PostProcessConfig> {
    const res = await authedFetch(
        requireServiceUrl(
            getBboxServiceUrl(host, `postprocess/presets/${presetId}/apply`, isSecure, arcId),
        ),
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ mode }),
        },
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as { postprocess?: PostProcessConfig };
    if (!data.postprocess) {
        throw new Error("Invalid apply preset response");
    }
    return data.postprocess;
}

export async function createPostprocessPreset(
    host: string,
    payload: { label: string; description?: string; from?: "current" },
    isSecure?: boolean,
    arcId?: string | null,
): Promise<{ preset: PostProcessPreset; presets: PostProcessPreset[] }> {
    const res = await authedFetch(
        requireServiceUrl(getBboxServiceUrl(host, "postprocess/presets", isSecure, arcId)),
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...payload, from: payload.from ?? "current" }),
        },
    );
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as {
        preset?: PostProcessPreset;
        presets?: PostProcessPreset[];
    };
    if (!data.preset || !Array.isArray(data.presets)) {
        throw new Error("Invalid create preset response");
    }
    return { preset: data.preset, presets: data.presets };
}
